import { subWeeks } from 'date-fns'

import {
  AnalyticsFiltersParams,
  AnalyticsPaginationParams
} from '@img-arena/consoles-api-streaming'
import {
  PastEvent,
  PastEventsIssueState
} from '@img-arena/consoles-api-streaming/dist/analytics/pastEvents'
import { SelectedFilters } from '@/components/table/base/FilterableTable/FilterableTable.types'
import {
  datesToALC,
  datesToLabel,
  separateALC
} from '@/components/table/filters/DateRangeFilter/utils'
import { streamingRestApi } from '@/service/streamingApi'

import { createBaseTableStore } from '../table'
import type { FetchApiArgs, FetchApiResponse } from '../table/table.types'

export type PastEventsSortAttribute = 'eventTitle' | 'startDate' | 'endDate'

type FiltersParams = 'dateRange' | 'operator' | 'sport' | 'property' | 'tournament' | 'state'

const getInitialFilters = (): SelectedFilters<FiltersParams> => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  const twoWeeksAgo = subWeeks(new Date(), 2)
  twoWeeksAgo.setHours(0, 0, 0, 0)

  return {
    dateRange: [
      {
        value: datesToALC([twoWeeksAgo, today]),
        label: datesToLabel([twoWeeksAgo, today])
      }
    ]
  }
}

const fetchPastEventsApiCallback = async (
  args: FetchApiArgs<object, PastEventsSortAttribute, FiltersParams>
): Promise<FetchApiResponse<PastEvent>> => {
  const { page, pageSize, filters, signal } = args

  const [fromDate, toDate] = separateALC(filters?.dateRange?.[0]?.value?.toString() ?? '')
  const operatorId = filters?.operator?.[0]?.value?.toString()
  const sportIdFilter = filters?.sport?.[0].value
  const propertyIdFilter = filters?.property?.[0].value
  const tournamentIdFilter = filters?.tournament?.[0].value

  const sportId =
    sportIdFilter !== null && sportIdFilter !== undefined ? Number(sportIdFilter) : undefined

  const propertyId =
    propertyIdFilter !== null && propertyIdFilter !== undefined
      ? Number(propertyIdFilter)
      : undefined

  const tournamentId =
    tournamentIdFilter !== null && tournamentIdFilter !== undefined
      ? Number(tournamentIdFilter)
      : undefined

  const issueReported = filters?.state?.[0].value?.toString() as PastEventsIssueState

  const queryParams: AnalyticsFiltersParams &
    AnalyticsPaginationParams & { issueReported?: PastEventsIssueState } = {
    fromDate,
    toDate,
    page,
    issueReported,
    pageSize,
    operatorId,
    sportId,
    propertyId,
    tournamentId
  }

  const {
    data: { content, ...restResponse }
  } = await streamingRestApi.endpoints.analytics.getPastEvents(queryParams, signal)

  return {
    content,
    totalElements: restResponse.totalElements,
    number: restResponse.number,
    size: restResponse.size
  }
}

export const usePastEventsStore = createBaseTableStore<
  PastEvent,
  PastEventsSortAttribute,
  FiltersParams,
  object
>({
  storeName: 'Past Events',
  fetchApi: fetchPastEventsApiCallback,
  features: {
    pagination: true,
    sorting: false,
    filtering: true
  },
  initialStateOverrides: {
    initialFilters: getInitialFilters()
  }
})
